import { useCallback } from 'react';
import { useMutation } from '@apollo/client';

import { CreatePublicContextSessionDocument } from 'src/gql/graphql';
import { storePublicSessionSecret } from 'src/components/kiosk-pro/kiosk-pro-shared/use-session-secret';
import { getPersistedKioskDeviceToken } from 'src/hooks/use-capture-kiosk-id';

type UseCreatePublicContextSessionReturnType = {
  createPublicContextSession: (dispensaryId: string) => Promise<string | undefined>;
  isLoading: boolean;
};

// ECOMM-10367: single entry point for creating a kiosk public context session.
// Every creating display must persist the one-time creator secret so the later
// getPublicContextSession poll can retrieve the JWT — without it, the display gets
// accessToken: null once the per-dispensary flag is on. Centralizing the
// create-then-store here keeps a per-call-site `storePublicSessionSecret` from being
// forgotten (a missed store previously broke modal/back-navigation login).
export function useCreatePublicContextSession(): UseCreatePublicContextSessionReturnType {
  const [createPublicContextSessionMutation, { loading }] = useMutation(CreatePublicContextSessionDocument);

  const createPublicContextSession = useCallback(
    async (dispensaryId: string): Promise<string | undefined> => {
      try {
        const result = await createPublicContextSessionMutation({
          variables: {
            dispensaryId,
            // ECOMM-13062: only a session created with this paired-kiosk proof can be claimed by
            // a consumer login once the trusted-claim flag is on. Unpaired devices send null.
            kioskDeviceToken: getPersistedKioskDeviceToken(),
          },
        });

        const session = result.data?.createPublicContextSession;

        if (session?.id) {
          storePublicSessionSecret(session.id, session.creatorSecret);
        }

        return session?.id ?? undefined;
      } catch (error) {
        // A failed create (network/GraphQL error) resolves to undefined so callers fall
        // through their existing `if (!sessionId)` handling (close modal / back to lock)
        // instead of throwing an unhandled rejection in a click handler on an unattended
        // kiosk. storePublicSessionSecret is already failure-safe on its own.
        console.error('Failed to create public context session:', error);
        return undefined;
      }
    },
    [createPublicContextSessionMutation]
  );

  return {
    createPublicContextSession,
    isLoading: loading,
  };
}
